import React, { useState, useEffect } from "react";
import { Modal }              from "react-bootstrap";
import { toast }              from "react-toastify";
import CloseBtn               from "assets/icons/Close-1.svg";
import PasscodeInput          from "components/PasscodeInput/PasscodeInput";
import WalletPasscodeService  from "services/wallet-passcode.service";
import AuthService            from "services/auth.service";
import "./Withdraw.scss";

const PasscodeSetupModal = ({ show, onDone, onCancel }) => {
  const [step, setStep]                 = useState("passcode");
  const [passcode, setPasscode]         = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError]               = useState(null);
  const [submitting, setSubmitting]     = useState(false);

  useEffect(() => {
    if (!show) return;

    setStep("passcode");
    setPasscode("");
    setConfirmation("");
    setError(null);
  }, [show]);

  const restart = (message) => {
    setError(message);
    setPasscode("");
    setConfirmation("");
    setStep("passcode");
  };

  const handlePasscodeComplete = (code) => {
    setError(null);
    setPasscode(code);
    setConfirmation("");
    setStep("confirm");
  };

  const handleConfirmationComplete = (code) => {
    setConfirmation(code);

    if (code !== passcode) {
      restart("Passcodes do not match. Please try again.");
      return;
    }

    submit(code);
  };

  const submit = (code) => {
    setSubmitting(true);

    WalletPasscodeService.setup({ passcode: code, passcode_confirmation: code }).then(
      (data) => onDone(data),
      (error_) => {
        const status  = error_.response && error_.response.status;
        const code_   = error_.response?.data?.code;
        const message = error_.response?.data?.error;

        if (status === 401) {
          AuthService.logout();
          window.location.replace("/eb/login");
          return;
        }

        if (code_ === "wallet_passcode_invalid" || code_ === "wallet_passcode_mismatch") {
          restart(message || "That passcode cannot be used.");
        } else if (code_ === "wallet_passcode_already_set") {
          // Someone set one in another tab or on the app since this modal opened.
          toast.error(message || "A wallet passcode is already set.");
          onCancel();
        } else if (status === 429) {
          restart(null);
          toast.error(message || "Too many attempts. Please try again in a moment.");
        } else {
          restart(null);
          toast.error(message || "Failed to save your passcode");
        }
      }
    ).then(() => {
      setSubmitting(false);
    });
  };

  const choosing = step === "passcode";

  return (
    <Modal className="wallet-passcode-modal" size="lg" show={ show } onHide={ onCancel } backdrop="static" centered>
      <Modal.Header className="p-0">
        <button type="button" className="close close-button position-relative pb-0" onClick={ onCancel }>
          <img src={ CloseBtn } alt="modal-close" className="w-30" />
        </button>
      </Modal.Header>

      <Modal.Body>
        <h4 className="fweight-700">
          { choosing ? "Set up your wallet passcode." : "Re-enter your passcode." }
        </h4>
        <p className="modal-subtitle">
          { choosing
            ? "Secure your withdrawals and bank account information with a 6-digit passcode. You will be required to enter this passcode each time you access your withdrawal details."
            : "Enter the same 6 digits again to confirm." }
        </p>

        <PasscodeInput
          key={ step }
          value={ choosing ? passcode : confirmation }
          onChange={ choosing ? setPasscode : setConfirmation }
          onComplete={ choosing ? handlePasscodeComplete : handleConfirmationComplete }
          invalid={ !!error }
          disabled={ submitting }
          autoFocus
        />

        { error && <div className="field-error text-center mt-3">{ error }</div> }
      </Modal.Body>

      <Modal.Footer className="d-block">
        <div className="modal-footer-actions">
          <button type="button" className="btn btn-cancel" onClick={ onCancel } disabled={ submitting }>
            Cancel
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PasscodeSetupModal;
